import { BaseContract, ChainContractsRegister } from "@gearbox-protocol/sdk";
import { iBytecodeRepositoryAbi } from "@gearbox-protocol/sdk/abi/310/iBytecodeRepository";
import {
  AddressProviderContract,
  BYTECODE_REPOSITORY,
  BytecodeRepositoryContract,
  CROSS_CHAIN_GOVERNANCE,
  CrossChainMultisigContract,
  INSTANCE_MANAGER,
  InstanceManagerContract,
} from "@gearbox-protocol/sdk/permissionless";
import { useEffect, useState } from "react";
import { Address, zeroAddress } from "viem";
import { ClientConfig } from "@/config/client";

export function useWeb3() {
  const [contracts, setContracts] = useState<{
    addressProvider?: AddressProviderContract;
    bytecodeRepository?: BytecodeRepositoryContract;
    instanceManager?: InstanceManagerContract;
    crossChainMultisig?: CrossChainMultisigContract;
    register?: ChainContractsRegister;
  }>({});

  useEffect(() => {
    async function initContracts() {
      try {
        const config = ClientConfig.getInstance();

        // Initialize AddressProvider first
        const addressProvider = new AddressProviderContract(
          config.addressProvider,
          config.client,
        );

        // Helper function to get contract address
        const getAddress = async (key: string): Promise<Address> => {
          try {
            return await addressProvider.getAddressOrRevert(key, 0n);
          } catch {
            return zeroAddress;
          }
        };

        // Initialize all addresses in parallel
        const [
          bytecodeRepositoryAddress,
          instanceManagerAddress,
          crossChainMultisigAddress,
        ] = await Promise.all([
          getAddress(BYTECODE_REPOSITORY),
          getAddress(INSTANCE_MANAGER),
          getAddress(CROSS_CHAIN_GOVERNANCE),
        ]);

        const register = new ChainContractsRegister(config.client);

        const bytecodeRepository = new BytecodeRepositoryContract(
          bytecodeRepositoryAddress,
          config.client,
        );

        register.setContract(
          bytecodeRepositoryAddress,
          bytecodeRepository as BaseContract<typeof iBytecodeRepositoryAbi>,
        );
        register.setAddressLabel(
          bytecodeRepositoryAddress,
          bytecodeRepository.name,
        );

        const instanceManager = new InstanceManagerContract(
          instanceManagerAddress,
          register,
        );

        const crossChainMultisig = new CrossChainMultisigContract(
          crossChainMultisigAddress,
          register,
        );

        setContracts({
          addressProvider,
          bytecodeRepository,
          instanceManager,
          crossChainMultisig,
          register,
        });
      } catch (error) {
        console.error("Failed to initialize contracts:", error);
      }
    }

    if (
      Object.values(contracts).length > 0 &&
      Object.values(contracts).every(contract => contract !== undefined)
    )
      return;

    initContracts();
  }, [contracts]);

  return {
    ...contracts,
  };
}
